/*
 * <yardage-note> — live "what you'll receive" blurb for fabric PDPs.
 *
 * The element is server-rendered (snippets/yardage-note.liquid) so the first
 * paint is correct, then this script keeps it in sync when the shopper changes
 * the variant or the quantity. It sits next to <variant-selects> inside the
 * product-info section, so it survives the theme's variant re-render (only
 * <variant-selects> and a handful of IDs are swapped on a same-product change).
 *
 * The yards figure is parsed from the variant's Size option value
 * (e.g. `8 Yards - 54" Wide`), so this works on every fabric product with no
 * per-product configuration.
 *
 * Copy rules mirror snippets/yardage-note.liquid — keep the two in sync.
 */
(function () {
  if (customElements.get('yardage-note')) return;

  var YARD_RE = /^(\d+)\s*Yards?\b/i;
  var SWATCH_RE = /swatch/i;
  var FAT_QUARTER_RE = /fat\s*quarter/i;
  var NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];

  var ICONS = {
    // Ruler — single continuous piece.
    ruler:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><rect x="2" y="8" width="20" height="8" rx="1.5"/><path d="M6 8v3M10 8v4M14 8v3M18 8v4"/></svg>',
    // Scissors — the multi-cut warning state.
    scissors:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M20 4 8.12 15.88M14.47 14.48 20 20M8.12 8.12 12 12"/></svg>',
    // Grid — swatch tiles.
    grid:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>',
  };

  function numberWord(n) {
    return n >= 0 && n <= 10 ? NUMBER_WORDS[n] : String(n);
  }

  // Parse a list of option value strings into { mode, yards }.
  // Swatch wins over fat quarter wins over yardage if (implausibly) several
  // are present.
  function parseOptionValues(values) {
    var yards = null;
    var swatch = false;
    var fatQuarter = false;
    for (var i = 0; i < values.length; i++) {
      var value = values[i];
      if (typeof value !== 'string') continue;
      if (SWATCH_RE.test(value)) swatch = true;
      if (FAT_QUARTER_RE.test(value)) fatQuarter = true;
      var match = value.match(YARD_RE);
      if (match) yards = parseInt(match[1], 10);
    }
    if (swatch) return { mode: 'swatch', yards: null };
    if (fatQuarter) return { mode: 'fatquarter', yards: null };
    if (yards && yards > 0) return { mode: 'yardage', yards: yards };
    return { mode: 'none', yards: null };
  }

  // Presentation for a given state. Returns null when nothing should render.
  function build(mode, yards, qty) {
    if (mode === 'swatch') {
      return {
        state: 'info',
        icon: ICONS.grid,
        html: "You'll receive <strong>" + numberWord(qty) + ' 5" × 5" ' + (qty === 1 ? 'swatch' : 'swatches') + '</strong>.',
      };
    }
    if (mode === 'fatquarter') {
      return {
        state: 'info',
        icon: ICONS.ruler,
        html:
          "You'll receive <strong>" +
          numberWord(qty) +
          ' 18" × 27" fat quarter' +
          (qty === 1 ? '' : 's') +
          '</strong>.',
      };
    }
    if (mode === 'yardage') {
      if (qty === 1) {
        return {
          state: 'info',
          icon: ICONS.ruler,
          html:
            "You'll receive <strong>one continuous " + yards + '-yard piece</strong> — ' + yards * 3 + ' ft long × 54" wide.',
        };
      }
      return {
        state: 'warn',
        icon: ICONS.scissors,
        html:
          "You'll receive <strong>" +
          numberWord(qty) +
          ' individual ' +
          yards +
          '-yard cuts</strong> — ' +
          numberWord(qty) +
          ' separate pieces, not one continuous length. Need one continuous piece? Select your total yardage above.',
      };
    }
    return null;
  }

  class YardageNote extends HTMLElement {
    connectedCallback() {
      this._abort = new AbortController();
      this._sectionId = this.dataset.sectionId || '';
      // Scope to this product form instance so a second render on the page
      // (sticky bar, quick-add, featured product) can't cross the wires.
      this._scope = this.closest('product-info') || this.closest('.product__info-container') || document;
      this._iconEl = this.querySelector('.yardage-note__icon');
      this._textEl = this.querySelector('.yardage-note__text');
      // Seeded from the server-rendered state so an unchanged first render is
      // a no-op (no reflow, no redundant screen-reader announcement).
      this._lastKey = this.dataset.key || '';
      this._variant = this._readSelectedVariant();

      var qtyInput = this._quantityInput();
      if (qtyInput) {
        qtyInput.addEventListener('change', this._onQtyChange.bind(this), { signal: this._abort.signal });
        // `input` covers manual typing; debounced so we don't thrash mid-keystroke.
        qtyInput.addEventListener('input', this._onQtyInput.bind(this), { signal: this._abort.signal });
      }

      var pubsub = window.FoxTheme && window.FoxTheme.pubsub;
      if (pubsub && pubsub.PUB_SUB_EVENTS && pubsub.PUB_SUB_EVENTS.variantChange) {
        this._unsubscribe = pubsub.subscribe(pubsub.PUB_SUB_EVENTS.variantChange, this._onVariantChange.bind(this));
      } else {
        // Fallback: react to option input changes delegated on the section.
        this._scope.addEventListener('change', this._onScopeChange.bind(this), { signal: this._abort.signal });
      }

      this.render();
    }

    disconnectedCallback() {
      if (this._abort) this._abort.abort();
      if (this._unsubscribe) this._unsubscribe();
      clearTimeout(this._debounce);
    }

    _readSelectedVariant() {
      var blob = this._scope.querySelector('variant-selects [data-selected-variant]');
      if (blob && blob.textContent.trim()) {
        try {
          return JSON.parse(blob.textContent);
        } catch (error) {
          return null;
        }
      }
      return null;
    }

    _quantityInput() {
      return this._scope.querySelector('quantity-input input[name="quantity"], input.quantity__input[name="quantity"]');
    }

    _currentQty() {
      var input = this._quantityInput();
      if (!input) return 1;
      var raw = (input.value || '').trim();
      var n = parseInt(raw, 10);
      if (!raw || isNaN(n) || n <= 0) return 1; // empty / 0 / NaN -> 1 for display
      var min = parseInt(input.getAttribute('min') || input.getAttribute('data-min') || '1', 10);
      if (!isNaN(min) && min > 0 && n < min) n = min; // clamp up to the rule minimum
      return n;
    }

    _optionValues() {
      // Prefer the resolved variant (authoritative, and carries availability),
      // otherwise read the currently-checked option inputs straight from the DOM.
      if (this._variant && Array.isArray(this._variant.options)) {
        if (this._variant.available === false) return null;
        return this._variant.options;
      }
      var variantSelects = this._scope.querySelector('variant-selects');
      if (!variantSelects) return null;
      var values = [];
      variantSelects.querySelectorAll('select').forEach(function (select) {
        var option = select.options[select.selectedIndex];
        if (option) values.push(option.value);
      });
      variantSelects.querySelectorAll('input[type="radio"]:checked').forEach(function (input) {
        values.push(input.value);
      });
      return values;
    }

    render() {
      var values = this._optionValues();
      var parsed = values ? parseOptionValues(values) : { mode: 'none', yards: null };
      var qty = this._currentQty();
      var key = parsed.mode === 'none' ? 'none' : parsed.mode + ':' + (parsed.yards || 0) + ':' + qty;

      if (key === this._lastKey) return; // nothing changed -> don't touch the DOM
      this._lastKey = key;
      this.dataset.key = key;

      var view = build(parsed.mode, parsed.yards, qty);
      if (!view) {
        this.hidden = true;
        if (this._textEl) this._textEl.textContent = '';
        return;
      }

      this.classList.remove('yardage-note--info', 'yardage-note--warn');
      this.classList.add('yardage-note--' + view.state);
      if (this._iconEl) this._iconEl.innerHTML = view.icon;
      if (this._textEl) this._textEl.innerHTML = view.html;
      this.hidden = false;
    }

    _onVariantChange(event) {
      var data = event && event.data;
      if (!data) return;
      // Only react to our own section's variant change.
      if (this._sectionId && data.sectionId != null && String(data.sectionId) !== String(this._sectionId)) return;
      if ('variant' in data) this._variant = data.variant;
      this.render();
    }

    _onScopeChange(event) {
      var target = event.target;
      if (target && target.closest && target.closest('variant-selects')) {
        this._variant = null; // force a fresh read of the option inputs
        this.render();
      }
    }

    _onQtyChange() {
      this.render();
    }

    _onQtyInput() {
      clearTimeout(this._debounce);
      this._debounce = setTimeout(this.render.bind(this), 150);
    }
  }

  customElements.define('yardage-note', YardageNote);
})();
